/**
 * Utility functions for determining similarity between objects and strings.
 * These functions support the broader resource relationship scoring system.
 * 
 * @fileoverview Text and object similarity calculation utilities
 * @module similarity-utils
 * 
 * @exports normalizeText
 * @exports jaccardSimilarity
 * @exports cosineSimilarity
 * @exports textToVector
 * @exports textSimilarity
 * @exports wordContainmentRatio
 * @exports extractWords
 * @exports extractAcronyms
 * @exports containsAnyWord
 * @exports levenshteinDistance
 * @exports longestCommonSubstring
 * @exports semanticSimilarity
 * @exports chunkedTextSimilarity
 */

/**
 * Common words to ignore in similarity calculations to reduce noise.
 * These words appear frequently in text but add little semantic value.
 * 
 * @constant {ReadonlyArray<string>}
 */
export const COMMON_WORDS_TO_IGNORE: ReadonlyArray<string> = Object.freeze([
  'a', 'an', 'the', 'and', 'or', 'but', 'of', 'to', 'in', 'on', 'at', 'by',
  'for', 'with', 'about', 'as', 'from', 'is', 'are', 'was', 'were', 'be',
  'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would',
  'shall', 'should', 'can', 'could', 'may', 'might', 'must', 'this', 'that',
  'these', 'those', 'it', 'its', 'they', 'them', 'their', 'we', 'us', 'our'
])

/**
 * Normalizes text for comparison by converting to lowercase, removing special characters,
 * and standardizing whitespace.
 * 
 * @param {string} text - The text to normalize
 * @returns {string} Normalized text with consistent formatting
 * @example
 * // Returns "hello world"
 * normalizeText("Hello, World!")
 */
export function normalizeText(text: string): string {
  if (!text) return ''
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')  // Replace special chars with spaces
    .replace(/\s+/g, ' ')      // Standardize whitespace
    .trim()
}

/**
 * Calculates Jaccard similarity coefficient between two sets.
 * Measures similarity as the size of intersection divided by size of union.
 * 
 * @param {ReadonlySet<T>} set1 - First set
 * @param {ReadonlySet<T>} set2 - Second set
 * @returns {number} Similarity score between 0 and 1
 * @example
 * // Returns 0.5
 * jaccardSimilarity(new Set([1, 2, 3]), new Set([2, 3, 4]))
 */
export function jaccardSimilarity<T>(set1: ReadonlySet<T>, set2: ReadonlySet<T>): number {
  if (set1.size === 0 && set2.size === 0) return 1
  if (set1.size === 0 || set2.size === 0) return 0
  
  // Calculate intersection size efficiently
  const smallerSet = set1.size < set2.size ? set1 : set2
  const largerSet = set1.size < set2.size ? set2 : set1
  
  let intersectionSize = 0
  for (const item of smallerSet) {
    if (largerSet.has(item)) {
      intersectionSize++
    }
  }
  
  const unionSize = set1.size + set2.size - intersectionSize
  
  return intersectionSize / unionSize
}

/**
 * Calculates cosine similarity between two word frequency vectors.
 * Higher values indicate greater similarity, with 1 being identical vectors.
 * 
 * @param {Readonly<Record<string, number>>} vec1 - First word frequency vector
 * @param {Readonly<Record<string, number>>} vec2 - Second word frequency vector
 * @returns {number} Similarity score between 0 and 1
 * @example
 * // Returns 0.707 (approximately)
 * cosineSimilarity({a: 1, b: 2}, {a: 2, c: 1})
 */
export function cosineSimilarity(
  vec1: Readonly<Record<string, number>>, 
  vec2: Readonly<Record<string, number>>
): number {
  // Early return for empty vectors
  if (Object.keys(vec1).length === 0 && Object.keys(vec2).length === 0) return 1
  if (Object.keys(vec1).length === 0 || Object.keys(vec2).length === 0) return 0
  
  let dotProduct = 0
  let magnitude1 = 0
  let magnitude2 = 0
  
  // Process vec1 keys first (more efficient than creating a combined key set)
  for (const key in vec1) {
    const val1 = vec1[key] ?? 0
    const val2 = vec2[key] ?? 0
    
    dotProduct += val1 * val2
    magnitude1 += val1 * val1
  }
  
  // magnitude2 = sum of squares over ALL vec2 keys (dotProduct + magnitude1
  // are already complete from the vec1 loop above)
  for (const key in vec2) {
    const val2 = vec2[key] ?? 0
    magnitude2 += val2 * val2
  }

  magnitude1 = Math.sqrt(magnitude1)
  magnitude2 = Math.sqrt(magnitude2)
  
  if (magnitude1 === 0 || magnitude2 === 0) return 0
  return dotProduct / (magnitude1 * magnitude2)
}

/**
 * Converts text to a word frequency vector, optionally filtering out common words.
 * 
 * @param {string} text - Text to convert
 * @param {boolean} [ignoreCommonWords=false] - Whether to filter out common words
 * @returns {Record<string, number>} Word frequency vector
 * @example
 * // Returns { "hello": 1, "world": 1 }
 * textToVector("Hello world!")
 */
export function textToVector(
  text: string, 
  ignoreCommonWords = false
): Record<string, number> {
  const normalized = normalizeText(text)
  const words = normalized.split(' ').filter(word => word.length > 0)
  const vector: Record<string, number> = {}
  
  for (const word of words) {
    if (ignoreCommonWords && COMMON_WORDS_TO_IGNORE.includes(word)) {
      continue
    }
    vector[word] = (vector[word] ?? 0) + 1
  }
  
  return vector
}

/**
 * Calculates text similarity using cosine similarity of word vectors.
 * 
 * @param {string} text1 - First text
 * @param {string} text2 - Second text
 * @param {boolean} [ignoreCommonWords=false] - Whether to ignore common words
 * @returns {number} Similarity score between 0 and 1
 * @example
 * // Returns a value between 0 and 1 indicating similarity
 * textSimilarity("Hello world", "Hello there world")
 */
export function textSimilarity(
  text1: string, 
  text2: string, 
  ignoreCommonWords = false
): number {
  const vec1 = textToVector(text1, ignoreCommonWords)
  const vec2 = textToVector(text2, ignoreCommonWords)
  
  return cosineSimilarity(vec1, vec2)
}

/**
 * Extracts words from text, optionally filtering by minimum length and common words.
 * 
 * @param {string} text - Text to extract words from
 * @param {Object} [options] - Extraction options
 * @param {number} [options.minLength=1] - Minimum word length to include
 * @param {boolean} [options.ignoreCommonWords=false] - Whether to filter out common words
 * @returns {string[]} Array of words
 * @example
 * // Returns ["hello", "world"]
 * extractWords("Hello world!", { minLength: 3 })
 */
export function extractWords(
  text: string, 
  options: { minLength?: number; ignoreCommonWords?: boolean } = {}
): string[] {
  const { minLength = 1, ignoreCommonWords = false } = options
  const normalized = normalizeText(text)
  
  return normalized
    .split(' ')
    .filter(word => 
      word.length >= minLength && 
      !(ignoreCommonWords && COMMON_WORDS_TO_IGNORE.includes(word))
    )
}

/**
 * Calculates the percentage of words from one text contained in another.
 * 
 * @param {string} sourceText - Source text to check words from
 * @param {string} targetText - Target text to check against
 * @param {Object} [options] - Comparison options
    * @param {number} [options.minWordLength=3] - Minimum word length to consider
    * @param {boolean} [options.ignoreCommonWords=true] - Whether to ignore common words
 * @returns {number} Percentage (0-1) of source words found in target
 * @example
 * // Returns a value between 0 and 1 indicating containment ratio
 * wordContainmentRatio("red blue green", "blue green yellow")
 */
export function wordContainmentRatio(
  sourceText: string, 
  targetText: string, 
  options: { minWordLength?: number; ignoreCommonWords?: boolean } = {}
): number {
  const { minWordLength = 3, ignoreCommonWords = true } = options
  
  const sourceWords = new Set(extractWords(sourceText, { 
    minLength: minWordLength,
    ignoreCommonWords
  }))
  
  const targetWords = new Set(extractWords(targetText, { 
    minLength: minWordLength,
    ignoreCommonWords
  }))
  
  if (sourceWords.size === 0) return 0
  
  let matchCount = 0
  for (const word of sourceWords) {
    if (targetWords.has(word)) matchCount++
  }
  
  return matchCount / sourceWords.size
}

/**
 * Detects acronyms in text based on common patterns.
 * 
 * @param {string} text - Text to extract acronyms from
 * @returns {string[]} Array of potential acronyms
 * @example
 * // Returns ["API", "SDK"]
 * extractAcronyms("The API and SDK documentation")
 */
export function extractAcronyms(text: string): string[] {
  // Match uppercase sequences of 2+ letters that aren't part of a longer word
  const acronymRegex = /\b[A-Z]{2,}\b/g
  return text.match(acronymRegex) ?? []
}

/**
 * Checks if a text contains any word from a list.
 * 
 * @param {string} text - Text to check
 * @param {ReadonlyArray<string>} words - Words to look for
 * @param {boolean} [ignoreCase=true] - Whether to ignore case when matching
 * @returns {boolean} True if any word is found
 * @example
 * // Returns true
 * containsAnyWord("Hello world", ["world", "universe"])
 */
export function containsAnyWord(
  text: string, 
  words: ReadonlyArray<string>,
  ignoreCase = true
): boolean {
  if (!text || words.length === 0) return false
  
  const normalizedText = ignoreCase ? normalizeText(text) : text
  
  // For small word lists, direct iteration is more efficient
  if (words.length < 10) {
    return words.some(word => {
      const normalizedWord = ignoreCase ? normalizeText(word) : word
      return normalizedText.includes(normalizedWord)
    })
  }
  
  // For larger word lists, build a Set for O(1) lookups
  const textWords = new Set(
    normalizedText.split(' ').filter(word => word.length > 0)
  )
  
  return words.some(word => {
    const normalizedWord = ignoreCase ? normalizeText(word) : word
    return textWords.has(normalizedWord)
  })
}

/**
 * Type guard to check if a value is a valid number.
 * 
 * @param {unknown} value - The value to check
 * @returns {value is number} True if the value is a valid number
 */
function isValidNumber(value: unknown): value is number {
  return typeof value === 'number' && !isNaN(value) && isFinite(value)
}

/**
 * Safely gets a value from an array at the specified index.
 * 
 * @param {ReadonlyArray<T>} array - The array to get the value from
 * @param {number} index - The index to access
 * @returns {T | undefined} The value at the index, or undefined if out of bounds
 */
function safeArrayGet<T>(array: ReadonlyArray<T>, index: number): T | undefined {
  if (index < 0 || index >= array.length) {
    return undefined
  }
  return array[index]
}

/**
 * Calculates Levenshtein (edit) distance between two strings.
 * 
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Edit distance (lower means more similar)
 * @example
 * // Returns 3
 * levenshteinDistance("kitten", "sitting")
 * 
 * @important This implementation uses dynamic programming with O(m*n) time and space complexity.
 * For very long strings, consider using a more memory-efficient implementation.
 */
export function levenshteinDistance(str1: string, str2: string): number {
  // Optimization: handle empty strings
  if (!str1.length) return str2.length
  if (!str2.length) return str1.length
  
  // Optimization: swap strings to use less memory if str2 is shorter
  if (str1.length > str2.length) {
    [str1, str2] = [str2, str1]
  }
  
  const m = str1.length
  const n = str2.length
  
  // Create arrays with explicit number types and initialize all values
  let prevRow = new Array<number>(n + 1).fill(0)
  let currRow = new Array<number>(n + 1).fill(0)
  
  // Initialize the first row
  for (let j = 0; j <= n; j++) {
    prevRow[j] = j
  }
  
  // Fill the matrix
  for (let i = 1; i <= m; i++) {
    currRow[0] = i
    
    for (let j = 1; j <= n; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1
      
      // Safely get values from arrays with proper type guards
      const deletionCost = safeArrayGet(prevRow, j)
      const insertionCost = safeArrayGet(currRow, j - 1)
      const substitutionCost = safeArrayGet(prevRow, j - 1)
      
      // Ensure all values are valid numbers before proceeding
      if (
        isValidNumber(deletionCost) &&
        isValidNumber(insertionCost) &&
        isValidNumber(substitutionCost)
      ) {
        currRow[j] = Math.min(
          deletionCost + 1,           // deletion
          insertionCost + 1,          // insertion
          substitutionCost + cost     // substitution
        )
      } else {
        // Fallback to a safe default if any value is invalid
        currRow[j] = Math.max(i, j)
      }
    }
    
    // Create new arrays for the next iteration instead of swapping
    const temp = prevRow
    prevRow = [...currRow]
    currRow = temp.fill(0)
  }
  
  // The result is in the last element of prevRow
  const result = safeArrayGet(prevRow, n)
  return isValidNumber(result) ? result : Math.max(str1.length, str2.length)
}

/**
 * Calculates normalized similarity based on Levenshtein distance.
 * 
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Similarity score between 0 and 1
 * @example
 * // Returns approximately 0.57
 * levenshteinSimilarity("kitten", "sitting")
 */
export function levenshteinSimilarity(str1: string, str2: string): number {
  if (!str1 && !str2) return 1
  if (!str1 || !str2) return 0
  
  const maxLength = Math.max(str1.length, str2.length)
  if (maxLength === 0) return 1
  
  const distance = levenshteinDistance(str1, str2)
  return 1 - (distance / maxLength)
}

/**
 * Finds common subsequences between two arrays.
 * 
 * @param {ReadonlyArray<T>} arr1 - First array
 * @param {ReadonlyArray<T>} arr2 - Second array
 * @param {Function} [equalityFn] - Optional equality function
 * @returns {T[][]} Array of common subsequences
 * @example
 * // Returns [[2, 3], [5]]
 * findCommonSubsequences([1, 2, 3, 4, 5], [0, 2, 3, 9, 5])
 * 
 * @important Time complexity is O(n*m) where n and m are the lengths of the arrays.
 */
export function findCommonSubsequences<T>(
  arr1: ReadonlyArray<T>, 
  arr2: ReadonlyArray<T>, 
  equalityFn: (a: T, b: T) => boolean = (a, b) => a === b
): T[][] {
  // Early return for empty arrays
  if (arr1.length === 0 || arr2.length === 0) return []
  
  type SequenceItem = { value: T; i: number; j: number }
  
  const subsequences: T[][] = []
  let currentSeq: SequenceItem[] = []
  
  // Build a map for arr2 elements to their indices for faster lookups
  const arr2Indices = new Map<T, number[]>()
  for (let j = 0; j < arr2.length; j++) {
    const item = arr2[j]
    if (item !== undefined) {
      if (!arr2Indices.has(item)) {
        arr2Indices.set(item, [])
      }
      arr2Indices.get(item)?.push(j)
    }
  }
  
  for (let i = 0; i < arr1.length; i++) {
    const item1 = arr1[i]
    if (item1 === undefined) continue
    
    // Find potential matches in arr2
    const potentialMatches: number[] = []
    
    // Check for exact matches first (optimization)
    if (arr2Indices.has(item1)) {
      potentialMatches.push(...(arr2Indices.get(item1) ?? []))
    } else {
      // If no exact match, check with equality function
      for (let j = 0; j < arr2.length; j++) {
        const item2 = arr2[j]
        if (item2 !== undefined && equalityFn(item1, item2)) {
          potentialMatches.push(j)
        }
      }
    }
    
    for (const j of potentialMatches) {
      // Check if we can extend a current sequence
      if (currentSeq.length > 0) {
        const lastItem = currentSeq[currentSeq.length - 1]
        if (lastItem && i === lastItem.i + 1 && j === lastItem.j + 1) {
          currentSeq.push({ value: item1, i, j })
        } else {
          // Start a new sequence
          if (currentSeq.length > 0) {
            subsequences.push(currentSeq.map(item => item.value))
          }
          currentSeq = [{ value: item1, i, j }]
        }
      } else {
        // Initialize the first sequence
        currentSeq = [{ value: item1, i, j }]
      }
    }
  }
  
  // Add the last sequence if it exists
  if (currentSeq.length > 0) {
    subsequences.push(currentSeq.map(item => item.value))
  }
  
  return subsequences
}

/**
 * Options for the Longest Common Substring algorithm.
 * 
 * @interface LCSOptions
 * @property {number} [minLength] - Minimum length of substring to consider
 * @property {boolean} [ignoreCase] - Whether to ignore case when comparing
 * @property {boolean} [normalize] - Whether to normalize strings before comparison
 */
export interface LCSOptions {
  /** Minimum length of substring to consider */
  minLength?: number
  /** Whether to ignore case when comparing */
  ignoreCase?: boolean
  /** Whether to normalize strings before comparison */
  normalize?: boolean
}

/**
 * Finds the longest common substring between two strings.
 * Optimized for longer strings using suffix array implementation.
 * 
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @param {LCSOptions} [options] - Configuration options
 * @returns {string} The longest common substring
 * @example
 * // Returns "world"
 * longestCommonSubstring("hello world", "say world")
 * 
 * @important Time complexity is O(n*m) where n and m are the lengths of the strings.
 * Space complexity is O(1) due to the sliding window approach.
 */
export function longestCommonSubstring(
  str1: string,
  str2: string,
  options: LCSOptions = {}
): string {
  const { minLength = 1, ignoreCase = true, normalize = true } = options
  
  // Apply normalization if requested
  let s1 = str1
  let s2 = str2
  
  if (normalize) {
    s1 = normalizeText(s1)
    s2 = normalizeText(s2)
  } else if (ignoreCase) {
    s1 = s1.toLowerCase()
    s2 = s2.toLowerCase()
  }
  
  // Early returns for empty strings or strings too short
  if (!s1 || !s2) return ''
  if (s1.length < minLength || s2.length < minLength) return ''
  
  // Ensure s1 is the shorter string for optimization
  if (s1.length > s2.length) {
    [s1, s2] = [s2, s1]
  }
  
  // Use a sliding window approach for better memory efficiency
  let longestSubstring = ''
  
  // For each position in s1
  for (let i = 0; i < s1.length; i++) {
    // Try increasing lengths
    for (let len = longestSubstring.length + 1; i + len <= s1.length; len++) {
      const candidate = s1.substring(i, i + len)
      
      // If s2 contains this substring and it's longer than our current best
      if (s2.includes(candidate) && candidate.length > longestSubstring.length) {
        longestSubstring = candidate
      } else {
        // If current length doesn't work, longer ones won't either
        break
      }
    }
  }
  
  // Only return if it meets the minimum length requirement
  return longestSubstring.length >= minLength ? longestSubstring : ''
}

/**
 * Result of a semantic similarity calculation.
 * 
 * @interface SemanticSimilarityResult
 * @property {number} score - Overall similarity score between 0 and 1
 * @property {Object} components - Breakdown of individual similarity components
 * @property {number} components.wordOverlap - Word overlap similarity
 * @property {number} components.phraseSimilarity - Phrase similarity
 * @property {number} components.structureSimilarity - Structure similarity
 * @property {number} components.keywordSimilarity - Keyword importance similarity
 */
export interface SemanticSimilarityResult {
  /** Overall similarity score between 0 and 1 */
  score: number
  /** Breakdown of individual similarity components */
  components: {
    /** Word overlap similarity */
    wordOverlap: number
    /** Phrase similarity */
    phraseSimilarity: number
    /** Structure similarity */
    structureSimilarity: number
    /** Keyword importance similarity */
    keywordSimilarity: number
  }
}

/**
 * Options for semantic similarity calculation.
 * 
 * @interface SemanticSimilarityOptions
 * @property {number} [minWordLength] - Minimum word length to consider
 * @property {boolean} [ignoreCommonWords] - Whether to ignore common words
 * @property {Object} [weights] - Custom weights for different similarity components
 * @property {number} [weights.wordOverlap] - Weight for word overlap (default: 0.3)
 * @property {number} [weights.phraseSimilarity] - Weight for phrase similarity (default: 0.3)
 * @property {number} [weights.structureSimilarity] - Weight for structure similarity (default: 0.2)
 * @property {number} [weights.keywordSimilarity] - Weight for keyword importance (default: 0.2)
 * @property {ReadonlyArray<string>} [importantKeywords] - Additional important keywords to consider
 */
export interface SemanticSimilarityOptions {
  /** Minimum word length to consider */
  minWordLength?: number
  /** Whether to ignore common words */
  ignoreCommonWords?: boolean
  /** Custom weights for different similarity components */
  weights?: {
    /** Weight for word overlap (default: 0.3) */
    wordOverlap?: number
    /** Weight for phrase similarity (default: 0.3) */
    phraseSimilarity?: number
    /** Weight for structure similarity (default: 0.2) */
    structureSimilarity?: number
    /** Weight for keyword importance (default: 0.2) */
    keywordSimilarity?: number
  }
  /** Additional important keywords to consider */
  importantKeywords?: ReadonlyArray<string>
}

/**
 * Calculates semantic similarity between two texts using multiple algorithms.
 * Optimized for longer texts and resource relationship scoring.
 * 
 * @param {string} text1 - First text
 * @param {string} text2 - Second text
 * @param {SemanticSimilarityOptions} [options] - Configuration options
 * @returns {SemanticSimilarityResult} Detailed similarity result
 * @example
 * // Returns a detailed similarity analysis
 * semanticSimilarity(
 *   "TypeScript is a strongly typed programming language",
 *   "TypeScript adds strong typing to JavaScript"
 * )
 * 
 * @important This function combines multiple similarity metrics and may be
 * computationally expensive for very large texts. Consider using chunkedTextSimilarity
 * for documents larger than 10,000 characters.
 */
export function semanticSimilarity(
  text1: string,
  text2: string,
  options: SemanticSimilarityOptions = {}
): SemanticSimilarityResult {
  const {
    minWordLength = 3,
    ignoreCommonWords = true,
    weights = {
      wordOverlap: 0.3,
      phraseSimilarity: 0.3,
      structureSimilarity: 0.2,
      keywordSimilarity: 0.2
    },
    importantKeywords = []
  } = options
  
  // Normalize weights to ensure they sum to 1
  const totalWeight = Object.values(weights).reduce((sum, w) => sum + (w ?? 0), 0)
  const normalizedWeights = {
    wordOverlap: (weights.wordOverlap ?? 0.3) / totalWeight,
    phraseSimilarity: (weights.phraseSimilarity ?? 0.3) / totalWeight,
    structureSimilarity: (weights.structureSimilarity ?? 0.2) / totalWeight,
    keywordSimilarity: (weights.keywordSimilarity ?? 0.2) / totalWeight
  }
  
  // 1. Word overlap similarity (using Jaccard similarity)
  const words1 = new Set(extractWords(text1, { minLength: minWordLength, ignoreCommonWords }))
  const words2 = new Set(extractWords(text2, { minLength: minWordLength, ignoreCommonWords }))
  const wordOverlap = jaccardSimilarity(words1, words2)
  
  // 2. Phrase similarity (using common n-grams)
  const getNGrams = (text: string, n: number): string[] => {
    const words = extractWords(text, { minLength: 1, ignoreCommonWords })
    const ngrams: string[] = []
    for (let i = 0; i <= words.length - n; i++) {
      ngrams.push(words.slice(i, i + n).join(' '))
    }
    return ngrams
  }
  
  // Get bigrams and trigrams
  const bigrams1 = new Set(getNGrams(text1, 2))
  const bigrams2 = new Set(getNGrams(text2, 2))
  const trigrams1 = new Set(getNGrams(text1, 3))
  const trigrams2 = new Set(getNGrams(text2, 3))
  
  // Calculate n-gram similarity
  const bigramSimilarity = jaccardSimilarity(bigrams1, bigrams2)
  const trigramSimilarity = jaccardSimilarity(trigrams1, trigrams2)
  const phraseSimilarity = (bigramSimilarity + trigramSimilarity) / 2
  
  // 3. Structure similarity (using word vectors)
  const vec1 = textToVector(text1, ignoreCommonWords)
  const vec2 = textToVector(text2, ignoreCommonWords)
  const structureSimilarity = cosineSimilarity(vec1, vec2)
  
  // 4. Keyword importance similarity
  let keywordSimilarity = 0
  
  if (importantKeywords.length > 0) {
    // Extract important keywords from both texts
    const extractKeywordImportance = (text: string): Record<string, number> => {
      const words = extractWords(text, { minLength: 1 })
      const result: Record<string, number> = {}
      
      for (const keyword of importantKeywords) {
        // Count occurrences of each important keyword
        const normalizedKeyword = normalizeText(keyword)
        const count = words.filter(w => w === normalizedKeyword).length
        result[normalizedKeyword] = count
      }
      
      return result
    }
    
    const keywordImportance1 = extractKeywordImportance(text1)
    const keywordImportance2 = extractKeywordImportance(text2)
    
    // Calculate similarity of keyword importance
    keywordSimilarity = cosineSimilarity(keywordImportance1, keywordImportance2)
  } else {
    // Extract potential keywords (longer words, acronyms)
    const extractPotentialKeywords = (text: string): string[] => {
      const longWords = extractWords(text, { minLength: minWordLength + 2, ignoreCommonWords })
      const acronyms = extractAcronyms(text)
      return [...longWords, ...acronyms]
    }
    
    const keywords1 = new Set(extractPotentialKeywords(text1))
    const keywords2 = new Set(extractPotentialKeywords(text2))
    
    keywordSimilarity = jaccardSimilarity(keywords1, keywords2)
  }
  
  // Calculate weighted score
  const components = {
    wordOverlap,
    phraseSimilarity,
    structureSimilarity,
    keywordSimilarity
  }
  
  const score = 
    wordOverlap * normalizedWeights.wordOverlap +
    phraseSimilarity * normalizedWeights.phraseSimilarity +
    structureSimilarity * normalizedWeights.structureSimilarity +
    keywordSimilarity * normalizedWeights.keywordSimilarity
  
  return {
    score,
    components
  }
}

/**
 * Options for chunked similarity calculation.
 * 
 * @interface ChunkedSimilarityOptions
 * @property {number} [chunkSize] - Maximum chunk size in characters
 * @property {number} [chunkOverlap] - Overlap between chunks in characters
 * @property {Function} [similarityFn] - Similarity function to use for chunk comparison
 */
export interface ChunkedSimilarityOptions {
  /** Maximum chunk size in characters */
  chunkSize?: number
  /** Overlap between chunks in characters */
  chunkOverlap?: number
  /** Similarity function to use for chunk comparison */
  similarityFn?: (a: string, b: string) => number
}

/**
 * Calculates similarity between very long texts by chunking them.
 * Optimized for performance with large documents.
 * 
 * @param {string} text1 - First text
 * @param {string} text2 - Second text
 * @param {ChunkedSimilarityOptions} [options] - Configuration options
 * @returns {number} Similarity score between 0 and 1
 * @example
 * // Returns similarity score for large documents
 * chunkedTextSimilarity(longDocument1, longDocument2)
 * 
 * @important This function breaks large texts into manageable chunks to avoid
 * memory issues. Time complexity is O(n) where n is the total text length.
 */
export function chunkedTextSimilarity(
  text1: string,
  text2: string,
  options: ChunkedSimilarityOptions = {}
): number {
  const {
    chunkSize = 1000,
    chunkOverlap = 200,
    similarityFn = textSimilarity
  } = options
  
  // Early returns for short texts
  if (text1.length < chunkSize && text2.length < chunkSize) {
    return similarityFn(text1, text2)
  }
  
  /**
   * Splits text into overlapping chunks.
   * 
   * @param {string} text - The text to split into chunks
   * @returns {string[]} Array of text chunks with overlap
   */
  const chunkText = (text: string): string[] => {
    const chunks: string[] = []
    let position = 0
    
    while (position < text.length) {
      const end = Math.min(position + chunkSize, text.length)
      chunks.push(text.substring(position, end))
      position += chunkSize - chunkOverlap
      
      // Ensure we make progress
      if (position >= text.length - 1 || position <= 0) break
    }
    
    return chunks
  }
  
  const chunks1 = chunkText(text1)
  const chunks2 = chunkText(text2)
  
  // Calculate similarity matrix between all chunks
  const similarityMatrix: number[][] = []
  
  for (const chunk1 of chunks1) {
    const row: number[] = []
    for (const chunk2 of chunks2) {
      row.push(similarityFn(chunk1, chunk2))
    }
    similarityMatrix.push(row)
  }
  
  // Find the best matching chunks
  let totalSimilarity = 0
  let matchCount = 0
  
  // For each chunk in text1, find its best match in text2
  for (const row of similarityMatrix) {
    if (row.length > 0) {
      totalSimilarity += Math.max(...row)
      matchCount++
    }
  }
  
  // Average similarity across all chunks
  return matchCount > 0 ? totalSimilarity / matchCount : 0
}

/**
 * Counts the number of words in a string by splitting on whitespace.
 * 
 * This function trims leading and trailing whitespace before counting
 * and treats any sequence of whitespace characters as a single word boundary.
 * 
 * @param {string} text - The input text to count words in
 * @returns {number} The number of words in the text
 * @example
 * // Returns 5
 * wordCount("This is a sample text")
 * 
 * // Returns 3
 * wordCount("  Multiple    spaces   between  ")
 */
export function wordCount(text: string): number {
  return text.trim().split(/\s+/).length
}
